import { useCallback, useEffect, useRef } from 'react';
import { useAnnotations } from './useRenderer';
import { useDispatch } from 'react-redux';
import { useCustomSelector } from '../../redux/store';
import { AnnotationProps } from '../../modules/renderer/RendererLeftPanel/Annotations/Annotation';
import { mergeSelectedAnnotations, toggleSelectedAnnotation } from '../../redux/rendererReducer';
import { isAnnotationContextGroup } from '../../contexts/AnnotationContext';

interface ShiftSelectContext {
  openingsByRoomId: Record<string, string[]>;
  openRooms: Set<string>;
}

export const useSelectAnnotation = (shiftContext?: ShiftSelectContext) => {
  const dispatch = useDispatch();
  const [{ indexedAnnotations }] = useAnnotations();
  const indexedAnnotationsRef = useRef(indexedAnnotations);
  useEffect(() => { indexedAnnotationsRef.current = indexedAnnotations; }, [indexedAnnotations]);
  const { lastSelectedAnnotation } = useCustomSelector((state) => state.rendererProvider, ['selectedAnnotations', 'lastSelectedAnnotation']);
  const lastSelectedAnnotationRef = useRef(lastSelectedAnnotation);
  useEffect(() => { lastSelectedAnnotationRef.current = lastSelectedAnnotation; }, [lastSelectedAnnotation]);
  const shiftContextRef = useRef(shiftContext);
  useEffect(() => { shiftContextRef.current = shiftContext; }, [shiftContext]);

  const onClickAnnotation: AnnotationProps['onClickAnnotation'] = useCallback(
    (selectedAnnotation, { isCtrl, isShift }) => {
      if (!isShift) return dispatch(toggleSelectedAnnotation({ ...selectedAnnotation, clearOtherSelections: !isCtrl }));
      const currentLastSelected = lastSelectedAnnotationRef.current;
      const currentIndexed = indexedAnnotationsRef.current;
      if (!currentLastSelected) return;

      const ctx = shiftContextRef.current;
      const openingsByRoomId = ctx?.openingsByRoomId ?? {};
      const openRooms = ctx?.openRooms ?? new Set<string>();

      const flatList: { identifier: string; type: 'group' | 'annotation'; groupIdentifier?: string; instanceKey?: string }[] = [];
      const pushAnnotation = (identifier: string, type: 'ROOM' | 'OTHER', groupIdentifier?: string) => {
        flatList.push({ identifier, type: 'annotation', groupIdentifier });
        if (type === 'ROOM' && openRooms.has(identifier)) {
          const openings = openingsByRoomId[identifier];
          if (openings) for (const openingId of openings) flatList.push({ identifier: openingId, type: 'annotation', instanceKey: identifier });
        }
      };

      for (const item of currentIndexed) {
        if (isAnnotationContextGroup(item)) {
          flatList.push({ identifier: item.identifier, type: 'group' });
          for (const child of item.annotations) {
            if (child.type === 'OPENING') continue;
            pushAnnotation(child.identifier, child.type === 'ROOM' ? 'ROOM' : 'OTHER', item.identifier);
          }
        } else {
          if (item.type === 'OPENING') continue;
          pushAnnotation(item.identifier, item.type === 'ROOM' ? 'ROOM' : 'OTHER');
        }
      }

      const findInstance = (id: string, instanceKey?: string) => {
        if (instanceKey !== undefined) {
          const idx = flatList.findIndex((item) => item.identifier === id && item.instanceKey === instanceKey);
          if (idx !== -1) return idx;
        }
        return flatList.findIndex((item) => item.identifier === id);
      };

      const lastSelectedIndex = findInstance(currentLastSelected.identifier, currentLastSelected.instanceKey);
      const newSelectedIndex = findInstance(selectedAnnotation.identifier, selectedAnnotation.instanceKey);
      if (lastSelectedIndex === -1 || newSelectedIndex === -1) return;

      return dispatch(
        mergeSelectedAnnotations({
          annotations: flatList.slice(Math.min(lastSelectedIndex, newSelectedIndex), Math.max(lastSelectedIndex, newSelectedIndex) + 1),
        }),
      );
    },
    [dispatch],
  );
  return { onClickAnnotation };
};
